import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Modal,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import Icon from '../components/Icon';
import { saveBiometricCredentials } from '../utilities/biometricStore';
import { getStoredTokens } from '../utilities/tokenStore';
import config from '../config';

export const BIOMETRIC_PROMPTED_KEY = '@eleplan/biometric_setup_prompted';

export async function shouldPromptBiometricSetup(): Promise<boolean> {
  const alreadyPrompted = await AsyncStorage.getItem(BIOMETRIC_PROMPTED_KEY);
  if (alreadyPrompted === 'true') return false;
  const hasHardware = await LocalAuthentication.hasHardwareAsync();
  const isEnrolled = await LocalAuthentication.isEnrolledAsync();
  return hasHardware && isEnrolled;
}

export async function markBiometricSetupPrompted(): Promise<void> {
  await AsyncStorage.setItem(BIOMETRIC_PROMPTED_KEY, 'true');
}

function extractClientIdFromJwt(token?: string): string {
  if (!token) return '';
  try {
    const payloadB64 = token.split('.')[1] || '';
    const padded = payloadB64 + '=='.slice(0, (4 - (payloadB64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload?.aud || payload?.client_id || '';
  } catch {
    return '';
  }
}

interface Props {
  visible: boolean;
  onDone: () => void;
}

export default function BiometricSetupModal({ visible, onDone }: Props) {
  const theme = useTheme();
  const sheet = useSheetLayout({ desktopMaxWidth: 480 });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const biometricLabel = Platform.OS === 'ios' ? 'Face ID / Touch ID' : 'Fingerprint';
  const biometricIcon: [string, string] = Platform.OS === 'ios' ? ['fad', 'face-viewfinder'] : ['fad', 'fingerprint'];

  const handleEnable = async () => {
    setStatus('loading');
    setErrorMsg('');
    try {
      const tokens = await getStoredTokens();
      if (!tokens?.refreshToken || !tokens?.username) {
        setStatus('error');
        setErrorMsg('Could not read session tokens. Please try again later.');
        return;
      }
      const resolvedClientId = tokens.refreshClientId
        || extractClientIdFromJwt(tokens.idToken)
        || config.cognito.appClientId
        || '';
      await saveBiometricCredentials(
        tokens.refreshToken,
        tokens.username,
        resolvedClientId
      );
      setStatus('success');
      dismissTimerRef.current = setTimeout(async () => {
        await markBiometricSetupPrompted();
        onDone();
      }, 1200);
    } catch (err: any) {
      const msg = err?.message || '';
      const cancelled = /cancel|dismiss|userCancel/i.test(msg);
      if (cancelled) {
        // User dismissed the biometric prompt — treat as "skip"
        setStatus('idle');
      } else {
        setStatus('error');
        setErrorMsg('Could not set up biometric login. You can enable it later in Settings.');
      }
    }
  };

  const handleSkip = async () => {
    await markBiometricSetupPrompted();
    onDone();
  };

  const s = useMemo(() => StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: theme.colors.background,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      paddingHorizontal: 28,
      paddingTop: 12,
      paddingBottom: 0,
    },
    handle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.colors.border,
      alignSelf: 'center',
      marginBottom: 28,
    },
    iconWrap: {
      width: 88,
      height: 88,
      borderRadius: 44,
      backgroundColor: 'rgba(238,108,76,0.12)',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
      marginBottom: 20,
    },
    title: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 10,
    },
    body: {
      fontSize: 15,
      color: theme.colors.muted,
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: 28,
    },
    primaryBtn: {
      height: 52,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primary,
      marginBottom: 12,
    },
    primaryBtnText: { fontSize: 16, fontWeight: '700', color: theme.colors.primaryText },
    skipBtn: {
      height: 44,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 8,
    },
    skipText: { fontSize: 15, color: theme.colors.muted },
    successRow: { flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 24 },
    successText: { fontSize: 15, fontWeight: '600', color: theme.colors.success || '#10B981' },
    errorText: { fontSize: 13, color: theme.colors.error, textAlign: 'center', marginBottom: 16 },
  }), [theme]);

  return (
    <Modal visible={visible} transparent animationType={sheet.animationType} onRequestClose={handleSkip}>
      <View style={[s.backdrop, sheet.overlayStyle]}>
        <SafeAreaView edges={['bottom']} style={[s.sheet, sheet.sheetStyle]}>
          {sheet.showHandle && <View style={s.handle} />}

          <View style={s.iconWrap}>
            <Icon icon={biometricIcon} size={44} color={theme.colors.primary} />
          </View>

          <Text style={s.title}>Enable {biometricLabel}?</Text>
          <Text style={s.body}>
            Sign in instantly without typing your password. Your credentials are stored securely on this device.
          </Text>

          {status === 'success' ? (
            <View style={s.successRow}>
              <Icon icon={['fas', 'circle-check']} size={20} color="#10B981" />
              <Text style={s.successText}>{biometricLabel} enabled</Text>
            </View>
          ) : (
            <>
              {status === 'error' && <Text style={s.errorText}>{errorMsg}</Text>}

              <TouchableOpacity
                style={s.primaryBtn}
                onPress={handleEnable}
                disabled={status === 'loading'}
                activeOpacity={0.85}
              >
                {status === 'loading' ? (
                  <ActivityIndicator color={theme.colors.primaryText} />
                ) : (
                  <Text style={s.primaryBtnText}>Enable {biometricLabel}</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity style={s.skipBtn} onPress={handleSkip} activeOpacity={0.7}>
                <Text style={s.skipText}>Not now</Text>
              </TouchableOpacity>
            </>
          )}
        </SafeAreaView>
      </View>
    </Modal>
  );
}
